// src/utils/ahiImageLoader.js
// Custom image loader for AWS HealthImaging
import * as cornerstone from 'cornerstone-core';

// Register a custom image loader for AHI images
export function registerAHIImageLoader() {
    cornerstone.registerImageLoader('ahi', loadAHIImage);
}

// Create a custom image ID for AHI images
export function createAHIImageId(imageSetId, frameId, metadata) {
    return `ahi:${imageSetId}/${frameId}`;
}

// Load image from AHI pixel data
function loadAHIImage(imageId) {
    try {
        // Parse the image ID
        const parts = imageId.split(':')[1].split('/');
        const imageSetId = parts[0];
        const frameId = parts[1];

        // Get the cached data
        const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
        if (!cachedData) {
            throw new Error('Image data not found in cache');
        }

        const { pixelData, metadata } = cachedData;

        // Determine the appropriate render function
        let renderFunction = cornerstone.renderGrayscaleImage;
        if (metadata.color || metadata.samplesPerPixel > 1) {
            renderFunction = cornerstone.renderColorImage;
        }

        // Create the image object for Cornerstone
        const image = {
            imageId: imageId,
            minPixelValue: metadata.minPixelValue || 0,
            maxPixelValue: metadata.maxPixelValue || 255,
            slope: metadata.rescaleSlope || 1,
            intercept: metadata.rescaleIntercept || 0,
            windowCenter: metadata.windowCenter || 128,
            windowWidth: metadata.windowWidth || 256,
            render: renderFunction,
            getPixelData: () => pixelData,
            rows: metadata.rows,
            columns: metadata.columns,
            height: metadata.rows,
            width: metadata.columns,
            color: metadata.color || metadata.samplesPerPixel > 1,
            columnPixelSpacing: metadata.pixelSpacing ? metadata.pixelSpacing[0] : 1,
            rowPixelSpacing: metadata.pixelSpacing ? metadata.pixelSpacing[1] : 1,
            sizeInBytes: pixelData.byteLength,
            rgba: metadata.samplesPerPixel === 4, // For canvas-decoded images
            getCanvas: metadata.color ? function () {
                // For color images, create a canvas
                const canvas = document.createElement('canvas');
                canvas.width = this.width;
                canvas.height = this.height;
                const ctx = canvas.getContext('2d');
                const imageData = ctx.createImageData(this.width, this.height);

                // Copy pixel data
                const data = this.getPixelData();
                for (let i = 0; i < data.length; i++) {
                    imageData.data[i] = data[i];
                }

                ctx.putImageData(imageData, 0, 0);
                return canvas;
            } : undefined
        };

        // Return in the format cornerstone expects
        return {
            promise: Promise.resolve(image),
            cancelFn: undefined
        };
    } catch (error) {
        // Return a rejected promise in the expected format
        return {
            promise: Promise.reject(error),
            cancelFn: undefined
        };
    }
}

// Modified downloadStudyAsFiles that works with AHI properly
export async function downloadStudyForViewing(healthImagingService, imageSetId, onProgress, onBatchReady) {
    try {
        console.log('Downloading study for viewing:', imageSetId);

        // Initialize cache if not exists
        if (!window.ahiImageCache) {
            window.ahiImageCache = {};
        }

        // Report initial progress
        if (onProgress) {
            onProgress({
                current: 0,
                total: 0,
                message: 'Fetching study metadata...',
                phase: 'metadata'
            });
        }

        // Get metadata first
        const metadata = await healthImagingService.getStudyMetadata(imageSetId);
        const imageFrames = await healthImagingService.getStudyImages(imageSetId);

        console.log(`Found ${imageFrames.length} SOP instances to download`);

        // Report that we know the total now
        if (onProgress) {
            onProgress({
                current: 0,
                total: imageFrames.length,
                message: `Preparing to download ${imageFrames.length} images...`,
                phase: 'downloading'
            });
        }

        const imageIds = [];
        const batchSize = 10; // Process images in batches of 10

        // Download each frame
        for (let i = 0; i < imageFrames.length; i++) {
            const frame = imageFrames[i];

            try {
                console.log(`Downloading SOP instance ${i + 1}/${imageFrames.length}...`);

                // Report progress
                if (onProgress) {
                    onProgress({
                        current: i + 1,
                        total: imageFrames.length,
                        message: `Downloading image ${i + 1} of ${imageFrames.length}`,
                        phase: 'downloading',
                        percentage: Math.round(((i + 1) / imageFrames.length) * 100)
                    });
                }

                // Get the pixel data
                const { imageBlob, contentType } = await healthImagingService.getImageFrame(
                    imageSetId,
                    frame.imageFrameId
                );

                // Convert blob to appropriate format
                let pixelData;

                if (imageBlob instanceof Blob) {
                    const arrayBuffer = await imageBlob.arrayBuffer();
                    pixelData = new Uint8Array(arrayBuffer);
                } else if (imageBlob instanceof ReadableStream) {
                    const reader = imageBlob.getReader();
                    const chunks = [];

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        chunks.push(value);
                    }

                    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
                    pixelData = new Uint8Array(totalLength);
                    let offset = 0;
                    for (const chunk of chunks) {
                        pixelData.set(chunk, offset);
                        offset += chunk.length;
                    }
                } else {
                    pixelData = new Uint8Array(imageBlob);
                }

                // Handle different content types
                if (contentType === 'video/mp4' || frame.transferSyntaxUID === '1.2.840.10008.1.2.4.102') {
                    // For MP4, we need to handle it differently
                    // Return a special marker that tells the viewer to use video mode
                    imageIds.push({
                        type: 'video',
                        imageSetId,
                        frameId: frame.imageFrameId,
                        pixelData,
                        metadata: frame,
                        contentType
                    });
                } else if (contentType === 'image/jpeg' || frame.transferSyntaxUID === '1.2.840.10008.1.2.4.50') {
                    // For JPEG, we need to decode the image data
                    // Create an image element to decode the JPEG
                    const blob = new Blob([pixelData], { type: 'image/jpeg' });
                    let imageUrl = null;

                    try {
                        imageUrl = URL.createObjectURL(blob);

                        // Create a promise to decode the JPEG
                        const decodePromise = new Promise((resolve, reject) => {
                            const img = new Image();
                            
                            const cleanup = () => {
                                if (imageUrl) {
                                    try {
                                        URL.revokeObjectURL(imageUrl);
                                    } catch (e) {
                                        // Ignore revoke errors - URL might already be revoked
                                    }
                                    imageUrl = null;
                                }
                            };

                            img.onload = function () {
                                try {
                                    // Create a canvas to get the pixel data
                                    const canvas = document.createElement('canvas');
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    const ctx = canvas.getContext('2d');
                                    ctx.drawImage(img, 0, 0);

                                    // Get the pixel data
                                    const imageData = ctx.getImageData(0, 0, img.width, img.height);
                                    const decodedPixelData = new Uint8Array(imageData.data.buffer);

                                    // Clean up blob URL immediately after use
                                    cleanup();

                                    // Create AHI image ID
                                    const imageId = createAHIImageId(imageSetId, frame.imageFrameId);

                                    // Cache the decoded data
                                    window.ahiImageCache[imageId] = {
                                        pixelData: decodedPixelData,
                                        metadata: {
                                            ...frame.dicomMetadata,
                                            rows: frame.rows || frame.dicomMetadata?.Rows || img.height,
                                            columns: frame.columns || frame.dicomMetadata?.Columns || img.width,
                                            windowCenter: frame.windowCenter || frame.dicomMetadata?.WindowCenter,
                                            windowWidth: frame.windowWidth || frame.dicomMetadata?.WindowWidth,
                                            pixelSpacing: frame.pixelSpacing,
                                            samplesPerPixel: 4, // RGBA from canvas
                                            rescaleSlope: frame.dicomMetadata?.RescaleSlope,
                                            rescaleIntercept: frame.dicomMetadata?.RescaleIntercept,
                                            minPixelValue: 0,
                                            maxPixelValue: 255,
                                            color: true // JPEG is color
                                        }
                                    };

                                    console.log(`Cached decoded JPEG image ${imageId} with metadata:`, window.ahiImageCache[imageId].metadata);

                                    resolve({
                                        type: 'image',
                                        imageId,
                                        instanceNumber: frame.instanceNumber,
                                        metadata: frame
                                    });
                                } catch (error) {
                                    cleanup();
                                    reject(error);
                                }
                            };

                            img.onerror = function (error) {
                                cleanup();
                                reject(new Error(`Failed to decode JPEG image: ${error.message || 'Unknown error'}`));
                            };

                            // Set a timeout to prevent hanging
                            const timeout = setTimeout(() => {
                                cleanup();
                                reject(new Error('JPEG decode timeout'));
                            }, 10000); // 10 second timeout

                            // Clear timeout on success/error
                            const originalOnload = img.onload;
                            const originalOnerror = img.onerror;
                            
                            img.onload = function(...args) {
                                clearTimeout(timeout);
                                originalOnload.apply(this, args);
                            };
                            
                            img.onerror = function(...args) {
                                clearTimeout(timeout);
                                originalOnerror.apply(this, args);
                            };

                            img.src = imageUrl;
                        });

                        // Wait for decoding to complete
                        const result = await decodePromise;
                        imageIds.push(result);
                        
                    } catch (error) {
                        // Ensure cleanup happens even if there's an error creating the blob URL
                        if (imageUrl) {
                            try {
                                URL.revokeObjectURL(imageUrl);
                            } catch (e) {
                                // Ignore cleanup errors
                            }
                        }
                        console.error(`Error processing JPEG frame ${frame.imageFrameId}:`, error);
                        // Continue with next frame instead of failing completely
                    }

                } else {
                    // For regular images, create AHI image ID
                    const imageId = createAHIImageId(imageSetId, frame.imageFrameId);

                    // Cache the data
                    window.ahiImageCache[imageId] = {
                        pixelData,
                        metadata: {
                            ...frame.dicomMetadata,
                            rows: frame.rows || frame.dicomMetadata?.Rows,
                            columns: frame.columns || frame.dicomMetadata?.Columns,
                            windowCenter: frame.windowCenter || frame.dicomMetadata?.WindowCenter,
                            windowWidth: frame.windowWidth || frame.dicomMetadata?.WindowWidth,
                            pixelSpacing: frame.pixelSpacing,
                            samplesPerPixel: frame.dicomMetadata?.SamplesPerPixel || 1,
                            rescaleSlope: frame.dicomMetadata?.RescaleSlope,
                            rescaleIntercept: frame.dicomMetadata?.RescaleIntercept,
                            minPixelValue: frame.dicomMetadata?.SmallestImagePixelValue,
                            maxPixelValue: frame.dicomMetadata?.LargestImagePixelValue
                        }
                    };

                    console.log(`Cached AHI image ${imageId} with metadata:`, window.ahiImageCache[imageId].metadata);

                    imageIds.push({
                        type: 'image',
                        imageId,
                        instanceNumber: frame.instanceNumber,
                        metadata: frame
                    });
                }

            } catch (error) {
                console.error(`Error downloading frame ${frame.imageFrameId}:`, error);
            }

            // Check if we should send a batch
            if (onBatchReady && (imageIds.length % batchSize === 0 || i === imageFrames.length - 1)) {
                // Calculate the start index for this batch
                const batchStart = Math.floor(imageIds.length / batchSize) * batchSize - batchSize;
                const batchEnd = imageIds.length;

                // Only send the NEW items in this batch
                const currentBatch = imageIds.slice(Math.max(0, batchStart), batchEnd);

                // Sort only the current batch by instance number
                currentBatch.sort((a, b) => {
                    const aNum = a.metadata?.instanceNumber || 0;
                    const bNum = b.metadata?.instanceNumber || 0;
                    return aNum - bNum;
                });

                // Send only the new batch to callback
                onBatchReady(currentBatch);
            }
        }

        // Sort by instance number
        imageIds.sort((a, b) => {
            const aNum = a.metadata?.instanceNumber || 0;
            const bNum = b.metadata?.instanceNumber || 0;
            return aNum - bNum;
        });

        console.log(`Successfully prepared ${imageIds.length} images for viewing`);
        return imageIds;

    } catch (error) {
        console.error('Error downloading study for viewing:', error);
        throw error;
    }
}

// Clean up cache when done
export function cleanupAHICache() {
    if (window.ahiImageCache) {
        delete window.ahiImageCache;
    }
}
